import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

export default function PlatformSecuritySettings() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowPermanentDeletion, setAllowPermanentDeletion] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get('platform/security-settings')
      .then((res) => {
        if (cancelled) return;
        setAllowPermanentDeletion(!!res.data?.data?.allow_permanent_school_deletion);
      })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: 'Error',
            description: 'Failed to load security settings',
            variant: 'destructive',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const handleToggle = async (checked) => {
    setSaving(true);
    const previous = allowPermanentDeletion;
    setAllowPermanentDeletion(checked);
    try {
      await api.put('platform/security-settings', {
        allow_permanent_school_deletion: checked,
      });
      toast({
        title: 'Saved',
        description: checked
          ? 'Permanent school deletion is now enabled in the Bin.'
          : 'Permanent school deletion is disabled.',
      });
    } catch (err) {
      setAllowPermanentDeletion(previous);
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to save security settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <CardContent className="p-6 space-y-6">
      <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
        <ShieldAlert className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground">
          <p className="font-medium text-foreground">Destructive actions</p>
          <p className="mt-1">
            Keep permanent deletion off unless you intentionally need to wipe a school. Deactivation
            still works without this toggle.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 rounded-xl border border-border p-4">
        <div className="space-y-1">
          <Label htmlFor="allow-permanent-school-deletion" className="text-base font-medium">
            Allow Permanent School Deletion
          </Label>
          <p className="text-sm text-muted-foreground">
            When ON, Super Admins can permanently delete schools from the Bin after email OTP
            verification. Default is OFF.
          </p>
        </div>
        <Switch
          id="allow-permanent-school-deletion"
          checked={allowPermanentDeletion}
          disabled={saving}
          onCheckedChange={handleToggle}
        />
      </div>
    </CardContent>
  );
}
